import { useCallback, useState } from "react";

import { inputModelToViewModelConverter, viewModelToInputModelConverter } from "@/components/features/_model/converter";

import { DEFAULT_ACCOMMODATION_HOTEL_POLICY_I18N_INPUT_MODEL } from "./constants/default-input-model";
import { excludeKeys, type AccommodationHotelPolicyI18nFormModel, type ExcludeKeys } from "./type";
import { hotelPolicyI18nInputZodSchema, type HotelPolicyI18nZodSchemaType } from "./validation-schema";
import { useSendTestEmailRequestReservationConfirmed } from "../../../_shared/repository/mutation/send-sample-email/request-reservation/confirmed";
import { useSendTestEmailRequestReservationDeclined } from "../../../_shared/repository/mutation/send-sample-email/request-reservation/declined";
import { useSendTestEmailRequestReservationRequested } from "../../../_shared/repository/mutation/send-sample-email/request-reservation/requested";
import { useSendTestEmailReservationCin } from "../../../_shared/repository/mutation/send-sample-email/reservation/c-in";
import { useSendTestEmailReservationCanceled } from "../../../_shared/repository/mutation/send-sample-email/reservation/canceled";
import { useSendTestEmailReservationCreated } from "../../../_shared/repository/mutation/send-sample-email/reservation/created";
import { useSendTestEmailReservationUpdated } from "../../../_shared/repository/mutation/send-sample-email/reservation/updated";

import type { AccommodationHotelPolicyI18nFormModelContextType } from "../ui/context";

type Props = {
	inputModel: AccommodationHotelPolicyI18nFormModel["InputModel"];

	queryData: AccommodationHotelPolicyI18nFormModel["QueryData"];
};

export const useInitAccommodationHotelPolicyI18nFormModelContext = ({
	inputModel: _inputModel,
	queryData,
}: Props): Omit<AccommodationHotelPolicyI18nFormModelContextType, "queryData" | "onUpdate"> & {
	resetViewModel: () => void;
} => {
	const { hotelID, lang, id } = _inputModel;
	const defaultInputModel = DEFAULT_ACCOMMODATION_HOTEL_POLICY_I18N_INPUT_MODEL({ queryData, hotelID, lang, id });
	const inputModel = _inputModel ?? defaultInputModel;

	const [viewModel, setViewModel] = useState(
		inputModelToViewModelConverter<
			AccommodationHotelPolicyI18nFormModel["InputModel"],
			ExcludeKeys,
			AccommodationHotelPolicyI18nFormModel["QueryData"],
			HotelPolicyI18nZodSchemaType
		>({
			defaultInput: defaultInputModel,
			initInput: inputModel,
			currentInput: inputModel,
			excludeKeys: excludeKeys,
			zodSchema: hotelPolicyI18nInputZodSchema,
		}),
	);

	const onSetViewModel: AccommodationHotelPolicyI18nFormModelContextType["onSetViewModel"] = useCallback(
		(values) => {
			const mergedViewModel = {
				...viewModel,
				...values.reduce(
					(acc, cur) => {
						acc[cur.key] = cur.value;
						return acc;
					},
					{} as Partial<AccommodationHotelPolicyI18nFormModel["ViewModel"]>,
				),
			};

			const currentInput = viewModelToInputModelConverter<
				AccommodationHotelPolicyI18nFormModel["InputModel"],
				ExcludeKeys,
				AccommodationHotelPolicyI18nFormModel["QueryData"]
			>(mergedViewModel);

			const newViewModel = inputModelToViewModelConverter<
				AccommodationHotelPolicyI18nFormModel["InputModel"],
				ExcludeKeys,
				AccommodationHotelPolicyI18nFormModel["QueryData"],
				HotelPolicyI18nZodSchemaType
			>({
				defaultInput: defaultInputModel,
				initInput: inputModel,
				currentInput,
				excludeKeys: excludeKeys,
				zodSchema: hotelPolicyI18nInputZodSchema,
			});

			setViewModel(newViewModel);
		},
		[defaultInputModel, inputModel, viewModel],
	);

	const resetViewModel = useCallback(() => {
		setViewModel({ ...viewModel, isDirty: false });
	}, [viewModel]);

	const { onSendTestEmailReservationCin } = useSendTestEmailReservationCin();
	const { onSendTestEmailReservationCanceled } = useSendTestEmailReservationCanceled();
	const { onSendTestEmailReservationCreated } = useSendTestEmailReservationCreated();
	const { onSendTestEmailReservationUpdated } = useSendTestEmailReservationUpdated();

	const { onSendTestEmailRequestReservationConfirmed } = useSendTestEmailRequestReservationConfirmed();
	const { onSendTestEmailRequestReservationDeclined } = useSendTestEmailRequestReservationDeclined();
	const { onSendTestEmailRequestReservationRequested } = useSendTestEmailRequestReservationRequested();

	return {
		viewModel,
		onSetViewModel,

		resetViewModel,

		testEmailSender: {
			onSendTestEmailRequestReservationConfirmed,
			onSendTestEmailRequestReservationDeclined,
			onSendTestEmailRequestReservationRequested,
			onSendTestEmailReservationCanceled,
			onSendTestEmailReservationCin,
			onSendTestEmailReservationCreated,
			onSendTestEmailReservationUpdated,
		},
	};
};
